import { createMatchDeck, getMatchRules } from './matchRules';
import type { ChallengeTier } from './types';

export const DAILY_MATCH_VERSION = 1;
export const DAILY_MATCH_TARGET = 75;
export const DAILY_MATCH_REWARD = 20;
export const DAILY_MATCH_PATH = '/game/daily-match';

export function localDateKey(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function dateOrdinal(date: string): number {
  const [year, month, day] = date.split('-').map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / 86400000);
}

export function validDateKey(date: unknown): date is string {
  if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const ordinal = dateOrdinal(date);
  return Number.isFinite(ordinal) && new Date(ordinal * 86400000).toISOString().slice(0, 10) === date;
}

export function shiftDate(date: string, days: number): string {
  return new Date((dateOrdinal(date) + days) * 86400000).toISOString().slice(0, 10);
}

export function seededRandom(seed: string) {
  let state = 2166136261;
  for (let i = 0; i < seed.length; i++) state = Math.imul(state ^ seed.charCodeAt(i), 16777619);
  return () => {
    state = (state + 0x6D2B79F5) | 0;
    let value = Math.imul(state ^ (state >>> 15), state | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export function getDailyMatch(date: string) {
  if (!validDateKey(date)) throw new Error('Invalid daily challenge date');
  const tier = ((dateOrdinal(date) % 3 + 3) % 3 + 1) as ChallengeTier;
  const seed = `daily-match-v${DAILY_MATCH_VERSION}:${date}`;
  const rules = getMatchRules('challenge', tier);
  return {
    date, tier, seed, rules, version: DAILY_MATCH_VERSION,
    title: ['位置成双', '辨清相似', '实空相映'][tier - 1],
    description: ['把图形与位置连在一起，找回完全相同的一对。', '同样的轮廓，不同的填充。仔细辨认，再翻下一张。', '形状与角标相同，一实一空，让两张牌恰好相遇。'][tier - 1],
  };
}

export function createDailyDeck(date: string) {
  const { rules, seed } = getDailyMatch(date);
  return createMatchDeck(rules.level, rules.pairs, seededRandom(seed));
}

export interface DailyMatchAttempt {
  sessionId: string;
  date: string;
  score: number;
  accuracy: number;
  completed: boolean;
  durationSeconds: number;
}

export interface DailyMatchRecord {
  date: string;
  version: number;
  tier: ChallengeTier;
  sessionIds: string[];
  attempts: number;
  firstScore: number;
  bestScore: number;
  bestAccuracy: number;
  completed: boolean;
  passed: boolean;
  durationSeconds: number;
}
export type DailyMatchRecords = Record<string, DailyMatchRecord>;

export function recordDailyMatch(records: DailyMatchRecords, attempt: DailyMatchAttempt) {
  const previous = records[attempt.date];
  const unchanged = { records, reward: 0, recorded: false, previousBest: previous?.bestScore ?? null };
  if (!validDateKey(attempt.date) || !attempt.sessionId || previous?.sessionIds.includes(attempt.sessionId)
    || !Number.isFinite(attempt.score) || attempt.score < 0 || attempt.score > 100
    || !Number.isFinite(attempt.accuracy) || attempt.accuracy < 0 || attempt.accuracy > 100
    || !Number.isFinite(attempt.durationSeconds) || attempt.durationSeconds < 0) return unchanged;
  const passed = attempt.completed && attempt.score >= DAILY_MATCH_TARGET;
  const record: DailyMatchRecord = {
    date: attempt.date, version: DAILY_MATCH_VERSION, tier: getDailyMatch(attempt.date).tier,
    sessionIds: [...(previous?.sessionIds ?? []), attempt.sessionId],
    attempts: (previous?.attempts ?? 0) + 1,
    firstScore: previous?.firstScore ?? attempt.score,
    bestScore: Math.max(previous?.bestScore ?? 0, attempt.score),
    bestAccuracy: Math.max(previous?.bestAccuracy ?? 0, attempt.accuracy),
    completed: Boolean(previous?.completed || attempt.completed),
    passed: Boolean(previous?.passed || passed),
    durationSeconds: (previous?.durationSeconds ?? 0) + attempt.durationSeconds,
  };
  return { records: { ...records, [attempt.date]: record }, reward: passed && !previous?.passed ? DAILY_MATCH_REWARD : 0, recorded: true, previousBest: previous?.bestScore ?? null };
}

export function dailyStreak(records: DailyMatchRecords, today: string): number {
  let day = records[today]?.passed ? today : shiftDate(today, -1);
  let streak = 0;
  while (records[day]?.passed) { streak++; day = shiftDate(day, -1); }
  return streak;
}

// Old installs have no daily data. Reject malformed entries rather than breaking the home screen.
export function sanitizeDailyRecords(value: unknown): DailyMatchRecords {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const records: DailyMatchRecords = {};
  for (const [date, raw] of Object.entries(value)) {
    if (!validDateKey(date) || !raw || typeof raw !== 'object') continue;
    const r = raw as DailyMatchRecord;
    if (r.date !== date || r.version !== DAILY_MATCH_VERSION || r.tier !== getDailyMatch(date).tier
      || !Array.isArray(r.sessionIds) || !r.sessionIds.every(id => typeof id === 'string' && id.length > 0)
      || !Number.isInteger(r.attempts) || r.attempts < 1 || r.attempts !== r.sessionIds.length
      || new Set(r.sessionIds).size !== r.sessionIds.length
      || ![r.firstScore, r.bestScore, r.bestAccuracy].every(n => Number.isFinite(n) && n >= 0 && n <= 100)
      || r.bestScore < r.firstScore || typeof r.completed !== 'boolean' || typeof r.passed !== 'boolean'
      || (r.passed && (!r.completed || r.bestScore < DAILY_MATCH_TARGET))
      || !Number.isFinite(r.durationSeconds) || r.durationSeconds < 0) continue;
    records[date] = r;
  }
  return records;
}
